import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { 
  FileText, 
  Loader2, 
  Copy, 
  Plus, 
  TrendingUp, 
  Brain,
  Stethoscope,
  Pill,
  Activity,
  Book,
  CheckCircle
} from "lucide-react";

interface ContentTemplate {
  id: string;
  name: string;
  description: string | null;
  template_type: string;
  template_content: string;
  variables: any;
  created_at: string;
}

interface TemplateParameter {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number';
  options?: string[];
  placeholder?: string;
  required?: boolean;
}

const specialtyIcons: Record<string, React.ComponentType<any>> = {
  cardiology: Activity,
  pharmacology: Pill,
  neurology: Brain,
  anatomy: Book,
  default: Stethoscope
};

export default function TemplateGenerator() {
  const [selectedTemplate, setSelectedTemplate] = useState<ContentTemplate | null>(null);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [showParameters, setShowParameters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [specialtyFilter, setSpecialtyFilter] = useState<string>("all");
  
  const queryClient = useQueryClient();

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['content-templates'],
    queryFn: async (): Promise<ContentTemplate[]> => {
      const { data, error } = await supabase
        .from('content_templates')
        .select('*')
        .order('usage_count', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesCategory = categoryFilter === "all" || template.template_type === categoryFilter;
      return matchesCategory;
    });
  }, [templates, categoryFilter]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [...new Set(templates.map(t => t.template_type))];
    return cats;
  }, [templates]);

  // Extract parameters from template prompt
  const extractParameters = (template: ContentTemplate): TemplateParameter[] => {
    const paramRegex = /\{(\w+)\}/g;
    const matches = [...template.template_content.matchAll(paramRegex)];
    const uniqueParams = [...new Set(matches.map(match => match[1]))];
    
    return uniqueParams.map(param => {
      // Define parameter types based on common parameter names
      if (param === 'difficulty') {
        return {
          key: param,
          label: 'Difficulty Level',
          type: 'select' as const,
          options: ['1', '2', '3', '4', '5'],
          required: true
        };
      } else if (param === 'detail_level') {
        return {
          key: param,
          label: 'Detail Level',
          type: 'select' as const,
          options: ['Basic', 'Intermediate', 'Advanced'],
          required: true
        };
      } else if (param === 'age_group') {
        return {
          key: param,
          label: 'Age Group',
          type: 'select' as const,
          options: ['Pediatric', 'Adult', 'Geriatric', 'All Ages'],
          required: false
        };
      } else if (param === 'question_type') {
        return {
          key: param,
          label: 'Question Type',
          type: 'select' as const,
          options: ['Clinical Scenario', 'Definition', 'Mechanism', 'Diagnosis', 'Treatment'],
          required: false
        };
      } else {
        return {
          key: param,
          label: param.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          type: 'text' as const,
          placeholder: `Enter ${param.replace(/_/g, ' ')}`,
          required: true
        };
      }
    });
  };

  // Generate content mutation
  const generateMutation = useMutation({
    mutationFn: async ({ templateId, params }: { templateId: string; params: Record<string, string> }) => {
      const { data, error } = await supabase.functions.invoke('ai-template-generator', {
        body: {
          templateId,
          parameters: params
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setGeneratedContent(data.content);
      setShowParameters(false);
      toast.success(`Content generated using ${data.template.name}! Used ${data.usage.tokensUsed} tokens ($${data.usage.estimatedCost.toFixed(4)})`);
      // Refresh templates to update usage count
      queryClient.invalidateQueries({ queryKey: ['content-templates'] });
    },
    onError: (error) => {
      console.error('Generation error:', error);
      toast.error('Failed to generate content');
    }
  });

  const handleTemplateSelect = (template: ContentTemplate) => {
    setSelectedTemplate(template);
    setGeneratedContent(null);
    const params = extractParameters(template);
    
    // Reset parameters
    const defaultParams: Record<string, string> = {};
    params.forEach(param => {
      if (param.options && param.options.length > 0) {
        defaultParams[param.key] = param.options[0];
      } else {
        defaultParams[param.key] = '';
      }
    });
    setParameters(defaultParams);
    setShowParameters(true);
  };

  const handleGenerate = () => {
    if (!selectedTemplate) return;
    
    const templateParams = extractParameters(selectedTemplate);
    const requiredParams = templateParams.filter(p => p.required);
    const missingParams = requiredParams.filter(p => !parameters[p.key]?.trim());
    
    if (missingParams.length > 0) {
      toast.error(`Please fill in required parameters: ${missingParams.map(p => p.label).join(', ')}`);
      return;
    }

    generateMutation.mutate({
      templateId: selectedTemplate.id,
      params: parameters
    });
  };

  const handleCopyContent = () => {
    if (generatedContent?.content) {
      navigator.clipboard.writeText(generatedContent.content);
      toast.success('Content copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Content Template Generator
          </CardTitle>
          <p className="text-muted-foreground">
            Generate structured medical content using AI-powered templates
          </p>
        </CardHeader>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label>Template Type</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredTemplates.map(template => {
          const IconComponent = specialtyIcons['default'];
          
          return (
            <Card 
              key={template.id} 
              className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-primary/20 hover:border-l-primary"
              onClick={() => handleTemplateSelect(template)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm mb-1 truncate">{template.name}</h3>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {template.description || 'No description'}
                    </p>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">
                        {template.template_type}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No templates found matching your filters</p>
          </CardContent>
        </Card>
      )}

      {/* Parameter Dialog */}
      <Dialog open={showParameters} onOpenChange={setShowParameters}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {selectedTemplate.description}
              </p>

              <div className="space-y-4">
                {extractParameters(selectedTemplate).map(param => (
                  <div key={param.key} className="space-y-2">
                    <Label>
                      {param.label}
                      {param.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    
                    {param.type === 'select' ? (
                      <Select 
                        value={parameters[param.key] || ''} 
                        onValueChange={(value) => setParameters(prev => ({ ...prev, [param.key]: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Select ${param.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {param.options?.map(option => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={parameters[param.key] || ''}
                        onChange={(e) => setParameters(prev => ({ ...prev, [param.key]: e.target.value }))}
                        placeholder={param.placeholder}
                        required={param.required}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowParameters(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleGenerate}
                  disabled={generateMutation.isPending}
                  className="flex items-center gap-2"
                >
                  {generateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Brain className="h-4 w-4" />
                  )}
                  Generate Content
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generated Content */}
      {generatedContent && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Generated Content
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyContent}
                className="flex items-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
              <div className="space-y-4">
                {selectedTemplate?.template_type === 'mcq' && generatedContent.question ? (
                // MCQ format
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium">Question:</Label>
                    <p className="mt-1">{generatedContent.question}</p>
                  </div>
                  
                  {generatedContent.options && (
                    <div>
                      <Label className="text-sm font-medium">Options:</Label>
                      <div className="mt-1 space-y-1">
                        {generatedContent.options.map((option: string, index: number) => (
                          <div 
                            key={index}
                            className={`p-2 rounded ${
                              index === generatedContent.correct_index 
                                ? 'bg-green-100 border border-green-300 dark:bg-green-900/20 dark:border-green-700' 
                                : 'bg-muted/50'
                            }`}
                          >
                            {String.fromCharCode(65 + index)}. {option}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {generatedContent.explanation && (
                    <div>
                      <Label className="text-sm font-medium">Explanation:</Label>
                      <p className="mt-1 text-sm">{generatedContent.explanation}</p>
                    </div>
                  )}
                </div>
              ) : (
                // Regular content format
                <Textarea
                  value={generatedContent.content || JSON.stringify(generatedContent, null, 2)}
                  readOnly
                  rows={15}
                  className="font-mono text-sm"
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}